import { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function AdminAgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [inviting, setInviting] = useState(false);
  const [deliveryNotice, setDeliveryNotice] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  const token = localStorage.getItem("adminToken");

  const fetchAgents = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/agents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => setAgents(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAgents, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    const res = await fetch(`${API_BASE}/admin/agents/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inviteForm),
    });
    const json = await res.json();
    setInviting(false);

    if (!res.ok || !json.success) {
      // show error message from server or fallback
      setInviteError(json.error?.message || "Failed to send invite");
      return;
    }

    setShowInviteModal(false);
    setInviteForm({ fullName: "", email: "", phone: "" });

    if (json.success) {
      const { delivery } = json.data;
      setDeliveryNotice({
        name: inviteForm.fullName,
        emailSent: delivery?.emailSent,
        whatsappSent: delivery?.whatsappSent,
        whatsappError: delivery?.whatsappError,
      });
      setTimeout(() => setDeliveryNotice(null), 8000);
    }
    fetchAgents();
  };

  const handleTogglePipeline = async (id) => {
    setOpenMenu(null);
    await fetch(`${API_BASE}/admin/agents/${id}/toggle-pipeline-visibility`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchAgents();
  };

  const handleDeactivate = async (id) => {
    setOpenMenu(null);
    if (
      !window.confirm(
        "Deactivate this agent? They will lose portal access immediately.",
      )
    )
      return;
    await fetch(`${API_BASE}/admin/agents/${id}/deactivate`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchAgents();
  };

  const handleResendInvite = async (id) => {
    setOpenMenu(null);
    await fetch(`${API_BASE}/admin/agents/${id}/resend-invite`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    alert("Invite link resent.");
  };

  const totalAgents = agents.length;
  const totalActiveApps = agents.reduce(
    (sum, a) => sum + a.activeApplications,
    0,
  );
  const pendingInvites = agents.filter(
    (a) => a.inviteStatus === "PENDING",
  ).length;
  const countries = new Set(agents.map((a) => a.nationality).filter(Boolean))
    .size;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        {deliveryNotice && (
          <div
            className={`mb-6 p-4 rounded-xl border flex items-start gap-3 ${
              deliveryNotice.emailSent || deliveryNotice.whatsappSent
                ? "bg-blue-50 border-blue-200"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <span className="material-symbols-outlined text-[#00255a]">
              info
            </span>
            <div className="text-sm">
              <p className="font-semibold text-[#0f172a] mb-1">
                Invite sent to {deliveryNotice.name}
              </p>
              <p className="text-[#44474f]">
                Email:{" "}
                {deliveryNotice.emailSent ? "✅ Sent" : "❌ Failed to send"}
                {" · "}
                WhatsApp:{" "}
                {deliveryNotice.whatsappSent ? "✅ Sent" : "❌ Failed to send"}
              </p>
              {deliveryNotice.whatsappError && (
                <p className="text-[#862026] text-xs mt-1">
                  WhatsApp error: {deliveryNotice.whatsappError}. If using
                  Twilio sandbox, confirm this number has sent the join code
                  first.
                </p>
              )}
            </div>
          </div>
        )}
        <header className="flex justify-between items-end mb-10">
          <div>
            <nav className="flex items-center gap-2 text-[#64748b] mb-2 text-sm">
              <span>Admin</span>
              <span className="material-symbols-outlined text-sm">
                chevron_right
              </span>
              <span className="text-[#00255a] font-semibold">
                Agent Management
              </span>
            </nav>
            <h2 className="text-3xl font-bold text-[#00255a]">
              Educational Agents
            </h2>
            <p className="text-[#64748b] max-w-xl mt-2">
              Oversee your global network of consulting agents and track their
              performance across recruitment pipelines.
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="text-white flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold shadow-md hover:scale-[1.02] transition-all"
            style={{
              background:
                "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)",
            }}
          >
            <span className="material-symbols-outlined">person_add</span>
            Invite New Agent
          </button>
        </header>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          <StatBox
            icon="group"
            iconBg="bg-[#00255a]/10"
            iconColor="text-[#00255a]"
            value={totalAgents}
            label="Total Agents"
          />
          <StatBox
            icon="rocket_launch"
            iconBg="bg-[#365da3]/10"
            iconColor="text-[#365da3]"
            value={totalActiveApps}
            label="Active Apps"
          />
          <StatBox
            icon="pending_actions"
            iconBg="bg-[#79161e]/10"
            iconColor="text-[#862026]"
            value={pendingInvites}
            label="Pending Invites"
          />
          <StatBox
            icon="public"
            iconBg="bg-[#00255a]/10"
            iconColor="text-[#00255a]"
            value={countries}
            label="Countries"
          />
        </div>
        {/* Agent Grid */}
        {loading ? (
          <p className="text-center text-[#64748b] py-20">Loading agents...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                menuOpen={openMenu === agent.id}
                onToggleMenu={() =>
                  setOpenMenu(openMenu === agent.id ? null : agent.id)
                }
                onTogglePipeline={() => handleTogglePipeline(agent.id)}
                onDeactivate={() => handleDeactivate(agent.id)}
                onResendInvite={() => handleResendInvite(agent.id)}
              />
            ))}
            <div
              onClick={() => setShowInviteModal(true)}
              className="bg-white/50 border-2 border-dashed border-[#e2e8f0] rounded-xl flex flex-col items-center justify-center p-12 text-center group hover:border-[#00255a] transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 rounded-full bg-[#00255a]/5 text-[#00255a] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined">add</span>
              </div>
              <h4 className="text-lg font-bold text-[#00255a] mb-1">
                Add New Partner
              </h4>
              <p className="text-[#64748b] max-w-[200px]">
                Expand your network with a new consultant
              </p>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
              <h3 className="text-xl font-bold text-[#00255a] mb-6">
                Invite New Agent
              </h3>
              <form onSubmit={handleInvite} className="space-y-4">
                <input
                  required
                  placeholder="Full Name"
                  value={inviteForm.fullName}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, fullName: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
                />
                <input
                  required
                  type="email"
                  placeholder="Email Address"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
                />
                {inviteError && (
                  <div className="mt-2 text-sm text-red-600 font-semibold">
                    {inviteError}
                  </div>
                )}
                <input
                  placeholder="Phone Number"
                  value={inviteForm.phone}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, phone: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
                />
                <p className="text-xs text-[#64748b]">
                  An invite link will be sent via email/WhatsApp for account
                  setup.
                </p>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className="flex-1 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
                    style={{
                      background:
                        "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)",
                    }}
                  >
                    {inviting ? "Sending..." : "Send Invite"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatBox({ icon, iconBg, iconColor, value, label }) {
  return (
    <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
      <div className={`inline-flex p-2 ${iconBg} ${iconColor} rounded-lg mb-4`}>
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <div className="text-3xl font-bold text-[#00255a]">{value}</div>
      <div className="text-xs text-[#64748b] uppercase font-bold mt-1">
        {label}
      </div>
    </div>
  );
}

function AgentCard({
  agent,
  menuOpen,
  onToggleMenu,
  onTogglePipeline,
  onDeactivate,
  onResendInvite,
}) {
  const isPending = agent.inviteStatus === "PENDING";

  return (
    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex gap-4">
            {agent.avatarUrl ? (
              <img
                className="w-16 h-16 rounded-full object-cover border-2 border-[#00255a]/10"
                src={agent.avatarUrl}
                alt={agent.fullName}
              />
            ) : (
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                  isPending
                    ? "bg-[#eceef0] text-[#747781] border-2 border-dashed border-[#c4c6d1]"
                    : "bg-[#8fb3ff] text-white"
                }`}
              >
                {isPending ? (
                  <span className="material-symbols-outlined text-3xl">
                    person
                  </span>
                ) : (
                  agent.fullName?.charAt(0)
                )}
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-[#00255a] leading-tight">
                {agent.fullName}
              </h3>
              <p className="text-[#64748b] text-sm">{agent.email}</p>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={onToggleMenu}
              className="p-2 hover:bg-[#f2f4f6] rounded-full text-[#64748b]"
            >
              <span className="material-symbols-outlined">more_vert</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-10 py-2">
                {!isPending && (
                  <button
                    onClick={onTogglePipeline}
                    className="w-full text-left px-4 py-2 hover:bg-[#f2f4f6] flex items-center gap-2 text-[#44474f] text-sm"
                  >
                    <span className="material-symbols-outlined text-sm">
                      visibility
                    </span>
                    Toggle Pipeline Visibility
                  </button>
                )}
                <button
                  onClick={onResendInvite}
                  className="w-full text-left px-4 py-2 hover:bg-[#f2f4f6] flex items-center gap-2 text-[#44474f] text-sm"
                >
                  <span className="material-symbols-outlined text-sm">
                    mail
                  </span>
                  Resend Invite
                </button>
                {!isPending && <hr className="my-1 border-[#e2e8f0]" />}
                {!isPending && agent.isActive && (
                  <button
                    onClick={onDeactivate}
                    className="w-full text-left px-4 py-2 hover:bg-red-50 flex items-center gap-2 text-red-600 text-sm"
                  >
                    <span className="material-symbols-outlined text-sm">
                      block
                    </span>
                    Deactivate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {agent.phone && (
            <div className="flex items-center gap-3 text-[#64748b] text-sm">
              <span className="material-symbols-outlined text-sm">call</span>
              <span>{agent.phone}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between py-4 border-t border-[#e2e8f0]">
          <div>
            <div
              className={`text-2xl font-bold ${isPending ? "text-[#64748b]" : "text-[#00255a]"}`}
            >
              {isPending ? "--" : agent.activeApplications}
            </div>
            <div className="text-xs text-[#64748b] uppercase font-bold tracking-wider">
              Active Apps
            </div>
          </div>
          {isPending ? (
            <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Invited
            </span>
          ) : agent.isActive ? (
            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{" "}
              Active
            </span>
          ) : (
            <span className="px-3 py-1 bg-gray-100 text-red-600 text-xs font-bold rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Not
              Active
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
